import {
  ReportNodeSchema,
  ReportPageSchema,
  TemplateLayoutTypeEnum,
  TemplateNodeTypeEnum,
} from '@ulysses-inc/harami_api_client'
import React, { ReactNode } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import { Link } from 'react-scroll'
import { HEADER_HEIGHT } from 'src/components/header/Header'
import filterNodesAnsweredBranch from 'src/exShared/util/reportResult/filterNodesAnsweredBranch'
import { isTableRowSectionNode } from 'src/features/reports/result/components/reportResult/TableSection'
import { WebReportResultProps } from 'src/features/reports/result/WebReportResultProps'
import { Primary } from 'src/features/theme/KdsThemeColor'
import { selectGridLayoutVisiblePageIds } from 'src/state/ducks/reports/reports/selectors'
import styled from 'styled-components'

const PageText = styled.p`
  :not(:first-child) {
    margin-top: 16px;
  }
`

const SideMenuLink = styled(Link)`
  color: ${Primary};
  cursor: pointer;
  font-size: 14px;
`

export const PagesLink: React.FC<WebReportResultProps> = props => {
  const isGridLayout = props.report.layoutType === TemplateLayoutTypeEnum.Grid

  // 表形式の場合、有効なページのみを表示する
  const gridLayoutVisiblePageIds = useSelector(
    selectGridLayoutVisiblePageIds,
    shallowEqual,
  )
  const pageIds = useSelector(() =>
    isGridLayout ? gridLayoutVisiblePageIds : props.report.pageIds,
  )

  const filterReportNodes = (nodeIds: number[]): ReactNode => {
    const allNodes = props.report.nodes

    return filterNodesAnsweredBranch(
      nodeIds
        .map((id: number) => allNodes[id])
        .filter((elem): elem is NonNullable<typeof elem> => !!elem),
      allNodes,
    ).map(node => {
      // 対象がない場合は表示しない
      if (!node) {
        return null
      }
      // セクション以外の場合は再帰的に探索する
      if (node.type !== TemplateNodeTypeEnum.Section) {
        return filterReportNodes(node.nodes)
      }
      // 行セクション名は行見出しなので出さない
      if (isTableRowSectionNode(node)) {
        return null
      }
      return renderSectionLink(node)
    })
  }

  const renderOriginalSectionLinks = (nodeIds: number[]) => {
    const allNodes = props.report.nodes
    return nodeIds
      .map(nodeId => allNodes[nodeId])
      .filter(node => !node?.section?.originalNodeUuid)
      .filter((node): node is ReportNodeSchema => !!node)
      .map(originalSectionNode => renderSectionLink(originalSectionNode))
  }

  const renderSectionLink = (node: ReportNodeSchema): ReactNode => {
    const section = node.section!

    return (
      <>
        <PageText key={`SectionLink-${node.id}`}>
          <SideMenuLink
            to={`Section-${node.id}`}
            spy={true}
            smooth={true}
            offset={-HEADER_HEIGHT}
            duration={200}
          >
            {section.name}
          </SideMenuLink>
        </PageText>
        {node.nodes && filterReportNodes(node.nodes)}
      </>
    )
  }

  const renderPageLink = (page: ReportPageSchema, index: number): ReactNode => {
    return (
      <>
        <PageText key={`PageLink-${page.id}`}>
          <SideMenuLink
            to={`Page-${page.id}`}
            spy={true}
            smooth={true}
            offset={-HEADER_HEIGHT}
            duration={200}
          >
            {page.name || `ページ${index + 1}`}
          </SideMenuLink>
        </PageText>
        {page.nodes &&
          (isGridLayout
            ? renderOriginalSectionLinks(page.nodes)
            : filterReportNodes(page.nodes))}
      </>
    )
  }

  return (
    <>
      {pageIds &&
        pageIds
          .map((pageId: number) => props.report.pages[pageId])
          .filter((page): page is ReportPageSchema => !!page)
          .map((page, index) => {
            return renderPageLink(page, index)
          })}
    </>
  )
}
